/**
 * Traffic Logger - Blogg App
 * 
 * Loggar sidvisningar till API för trafikanalys.
 * Kör i bakgrunden och ska aldrig blockera användarupplevelsen.
 */

import { getApiUrl } from './api';

interface TrafficEvent {
  app: 'web' | 'blog' | 'admin';
  path: string;
  sessionId?: string;
  referrer?: string;
}

// Generate or retrieve session ID for tracking sessions
let sessionId: string | undefined;

function getSessionId(): string {
  if (!sessionId) {
    // Try to get from sessionStorage
    const stored = sessionStorage.getItem('traffic_session_id');
    if (stored) {
      sessionId = stored;
    } else {
      // Generate new session ID
      sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      sessionStorage.setItem('traffic_session_id', sessionId);
    }
  }
  return sessionId;
}

/**
 * Log page view for traffic analytics.
 * Runs in background, does not block user experience.
 * Silently fails if API is unavailable.
 */
export async function logPageView(event: TrafficEvent): Promise<void> {
  // Skip in development if preferred (can be configured)
  const skipInDev = import.meta.env.VITE_SKIP_TRAFFIC_LOGGING === 'true';
  if (skipInDev && import.meta.env.DEV) {
    return;
  }

  try {
    // Don't await - fire and forget
    fetch(getApiUrl('traffic/log'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...event,
        sessionId: getSessionId(),
        referrer: document.referrer || undefined,
      }),
      // Don't include credentials - this should work for anonymous users
      // Use keepalive to ensure request completes even if user navigates away
      keepalive: true,
    }).catch(() => {
      // Silently fail - traffic logging should never break the app
    });
  } catch {
    // Ignore errors
  }
}

/**
 * Hook version for easier use in React components
 */
export function useTrafficLogger(app: 'web' | 'blog' | 'admin') {
  return (path: string) => {
    logPageView({ app, path });
  };
}
